import { FC } from 'react';
import { DescriptionList } from '@doda/io-react/common';
import { SEARCH_CONDITION_ITEM_TITLE } from '@doda/common/src/constants/screenStructure/searchConditionItems';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { ModalOverlay } from 'components/ModalOverlay';
import { createOccupationDisplayData } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import {
  AreaResponse,
  IndustryLResponse,
  JobTypeLResponse,
} from '@doda/api-client/aspida/@types';
import {
  createDisplayArea,
  createDisplayCity,
} from 'components/LocationStationModal/LocationModal/logics/createLocationDisplayData';
import { createDisplayStation } from 'components/LocationStationModal/StationModal/logics/createStationDisplayData';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { CompanyOfSearchModal } from '@doda/common/src/models/MasterCacheModel';
import { useEachModalJobCount } from 'hooks/useEachModalJobCount';
import { createIndustryDisplayData } from 'components/IndustryModal/logics/createIndustryDisplayData';
import dynamic from 'next/dynamic';
import styles from './index.module.scss';
import {
  EmploymentStatusHooksType,
  IncomeHooksType,
  IndustryHooksType,
  KeywordHooksType,
  LocationHooksType,
  OccupationHooksType,
  PickUpConditionHooksType,
  RouteStationHooksType,
} from './types';
import { OccupationContents } from './components/OccupationContents';
import { LocationContents } from './components/LocationContents';
import { IndustryContents } from './components/IndustryContents';
import { EmploymentContents } from './components/EmploymentContents';
import { IncomeContents } from './components/IncomeContents';
import { KeywordContents } from './components/KeywordContents';
import { KodawariContents } from './components/KodawariContents';

/**
 * SearchConditionSettingFormコンポーネントに渡されるプロパティを定義するインターフェース
 */
interface Props {
  /** 現在選択されている検索条件 */
  selectedCurrentCondition: SelectedCurrentConditionType;
  /** 求人数を更新する関数 */
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
  /** 各条件フックの関数 */
  conditionHooksFunctions: {
    occupation: OccupationHooksType;
    location: LocationHooksType;
    routeStation: RouteStationHooksType;
    industry: IndustryHooksType;
    employmentStatus: EmploymentStatusHooksType;
    income: IncomeHooksType;
    pickUpCondition: PickUpConditionHooksType;
    includeKeyword: KeywordHooksType;
    excludeKeyword: KeywordHooksType;
  };
  /** モーダル設定ボタンのコールバック関数 */
  onClickModalSettingButtonFunctions: {
    onClickOccupationModalSettingButton: (selectedOccupation: {
      lList: SelectedCurrentConditionType['searchJobTypeLId'];
      mList: SelectedCurrentConditionType['searchJobTypeMId'];
      sList: SelectedCurrentConditionType['searchJobTypeSId'];
    }) => void;
    onClickLocationModalSettingButton: (selectedLocation: {
      areaList: string[];
      prefectureList: string[];
      cityList: string[];
      wardList: string[];
    }) => void;
    onClickRouteStationModalSettingButton: (selectedRouteStation: {
      routeList: string[];
      stationList: string[];
    }) => void;
    onClickIndustryModalSettingButton: (selectedIndustry: {
      lList: string[];
      sList: string[];
    }) => void;
  };
  /** モーダルの求人件数を更新する関数 */
  updateModalJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
  /** モーダルの状態を管理するオブジェクト */
  modalFunctions: Record<
    string,
    {
      isOpen: boolean;
      onOpen: () => void;
      onClose: () => void;
      onToggle: () => void;
    }
  >;
  /** モーダルを開くコールバック関数 */
  onClickOpenModalFunctions: {
    onClickLocationModal: () => void;
    onClickStationModal: () => void;
  };
  /** モーダルの合計求人件数 */
  modalTotalCount: number;
  /** マスターデータ */
  masterData: {
    occupation: JobTypeLResponse[];
    area: AreaResponse[];
    route: CompanyOfSearchModal[];
    industry: IndustryLResponse[];
  };
}

// モーダルの遅延読み込み
const OccupationModal = dynamic(
  () => import('./intermediateExport/occupationModal'),
  { ssr: false }
);
const LocationModal = dynamic(
  () => import('./intermediateExport/locationModal'),
  { ssr: false }
);
const StationModal = dynamic(
  () => import('./intermediateExport/stationModal'),
  { ssr: false }
);
const IndustryModal = dynamic(
  () => import('./intermediateExport/IndustryModal'),
  { ssr: false }
);

/**
 * SearchConditionSettingFormは検索条件を設定するフォームを表示するコンポーネント
 *
 * @param {Props} props - プロパティ
 * @returns {JSX.Element} - 検索条件設定フォームの要素
 */
export const SearchConditionSettingForm: FC<Props> = ({
  selectedCurrentCondition,
  updateJobCount,
  conditionHooksFunctions,
  onClickModalSettingButtonFunctions,
  updateModalJobCount,
  modalFunctions,
  onClickOpenModalFunctions,
  modalTotalCount,
  masterData,
}) => {
  const {
    occupation,
    location,
    routeStation,
    industry,
    employmentStatus,
    income,
    pickUpCondition,
    includeKeyword,
    excludeKeyword,
  } = conditionHooksFunctions;

  const {
    onClickOccupationModalSettingButton,
    onClickLocationModalSettingButton,
    onClickRouteStationModalSettingButton,
    onClickIndustryModalSettingButton,
  } = onClickModalSettingButtonFunctions;

  const { locationModal, stationModal } = modalFunctions;
  const occupationModal = useDisclosure();
  const industryModal = useDisclosure();

  const { onClickLocationModal, onClickStationModal } =
    onClickOpenModalFunctions;

  const {
    occupationModalJobCount,
    locationAndStationModalJobCount,
    industryModalJobCount,
    getJobCountWithoutOccupation,
    getJobCountWithoutLocationAndStation,
    getJobCountWithoutIndustry,
  } = useEachModalJobCount(selectedCurrentCondition);

  return (
    <>
      <DescriptionList
        type="column2"
        column2dataBgColor="gray"
        className={styles.searchConditionSettingForm}
        listData={[
          {
            title: SEARCH_CONDITION_ITEM_TITLE.OCCUPATION,
            contents: (
              <OccupationContents
                occupation={occupation}
                updateJobCount={updateJobCount}
                onClickOpenModal={() => {
                  getJobCountWithoutOccupation();
                  occupationModal.onOpen();
                }}
              />
            ),
          },
          {
            title: SEARCH_CONDITION_ITEM_TITLE.LOCATION,
            contents: (
              <LocationContents
                location={location}
                routeStation={routeStation}
                updateJobCount={updateJobCount}
                onClickOpenLocationModal={() => {
                  getJobCountWithoutLocationAndStation();
                  onClickLocationModal();
                }}
                onClickOpenStationModal={() => {
                  getJobCountWithoutLocationAndStation();
                  onClickStationModal();
                }}
              />
            ),
          },
          {
            title: SEARCH_CONDITION_ITEM_TITLE.INDUSTRY,
            contents: (
              <IndustryContents
                industry={industry}
                updateJobCount={updateJobCount}
                onClickOpenModal={() => {
                  getJobCountWithoutIndustry();
                  industryModal.onOpen();
                }}
              />
            ),
          },
          {
            title: SEARCH_CONDITION_ITEM_TITLE.EMPLOYMENT,
            contents: (
              <EmploymentContents
                employmentStatus={employmentStatus}
                updateJobCount={updateJobCount}
              />
            ),
          },
          {
            title: SEARCH_CONDITION_ITEM_TITLE.SALARY,
            contents: (
              <IncomeContents income={income} updateJobCount={updateJobCount} />
            ),
          },
          {
            title: SEARCH_CONDITION_ITEM_TITLE.KODAWARI,
            contents: (
              <KodawariContents
                pickUpCondition={pickUpCondition}
                updateJobCount={updateJobCount}
              />
            ),
          },
          {
            title: SEARCH_CONDITION_ITEM_TITLE.KEYWORD,
            contents: (
              <KeywordContents
                includeKeyword={includeKeyword}
                excludeKeyword={excludeKeyword}
                updateJobCount={updateJobCount}
              />
            ),
          },
        ]}
      />
      <ModalOverlay
        show={
          occupationModal.isOpen ||
          locationModal.isOpen ||
          stationModal.isOpen ||
          industryModal.isOpen
        }
        onClickOverlay={() => {
          occupationModal.onClose();
          locationModal.onClose();
          stationModal.onClose();
          industryModal.onClose();
        }}
      >
        {occupationModal.isOpen && (
          <OccupationModal
            show={occupationModal.isOpen}
            onClickClose={occupationModal.onClose}
            onClickSearch={(checkedState) => {
              onClickOccupationModalSettingButton({
                lList: checkedState.L,
                mList: checkedState.M,
                sList: checkedState.S,
              });
              occupationModal.onClose();
            }}
            onChangeSelectedOccupation={(checkedState) => {
              updateModalJobCount({
                searchJobTypeLId: checkedState.L,
                searchJobTypeMId: checkedState.M,
                searchJobTypeSId: checkedState.S,
              });
            }}
            displayOccupationData={createOccupationDisplayData(
              masterData.occupation,
              occupationModalJobCount
            )}
            matchJobCount={modalTotalCount}
            selectedOccupationIdList={{
              L: selectedCurrentCondition.searchJobTypeLId,
              M: selectedCurrentCondition.searchJobTypeMId,
              S: selectedCurrentCondition.searchJobTypeSId,
            }}
          />
        )}
        {locationModal.isOpen && (
          <LocationModal
            show={locationModal.isOpen}
            onClickClose={locationModal.onClose}
            initialTotalCount={modalTotalCount}
            selectedLocationModalIdList={{
              checkedArea: selectedCurrentCondition.areaId,
              checkedPref: selectedCurrentCondition.prefectureId,
              checkedCity: selectedCurrentCondition.cityId,
              checkedWard: selectedCurrentCondition.wardId,
            }}
            areaList={createDisplayArea(
              masterData.area,
              locationAndStationModalJobCount
            )}
            cityList={createDisplayCity(
              masterData.area,
              locationAndStationModalJobCount
            )}
            onClickSearch={(checkedState) => {
              onClickLocationModalSettingButton({
                areaList: checkedState.checkedAllArea,
                prefectureList: checkedState.checkedAllPref,
                cityList: checkedState.checkedAllCity,
                wardList: checkedState.checkedWard,
              });
              locationModal.onClose();
            }}
            onChangeSelectedLocation={(checkedState) => {
              updateModalJobCount({
                areaId: checkedState.checkedAllArea,
                prefectureId: checkedState.checkedAllPref,
                cityId: checkedState.checkedAllCity,
                wardId: checkedState.checkedWard,
              });
            }}
          />
        )}
        {stationModal.isOpen && (
          <StationModal
            show={stationModal.isOpen}
            onClickClose={stationModal.onClose}
            initialTotalCount={modalTotalCount}
            selectedStationModalIdList={{
              stationList: selectedCurrentCondition.stationId,
              routeList: selectedCurrentCondition.routeId,
            }}
            areaList={createDisplayArea(
              masterData.area,
              locationAndStationModalJobCount
            )}
            railwayCompanyList={createDisplayStation(
              masterData.route,
              locationAndStationModalJobCount
            )}
            onClickSearch={(checkedState) => {
              onClickRouteStationModalSettingButton({
                routeList: checkedState.checkedAllRoute,
                stationList: checkedState.checkedStation,
              });
              stationModal.onClose();
            }}
            onChangeSelectedStation={(checkedState) => {
              updateModalJobCount({
                routeId: checkedState.checkedAllRoute,
                stationId: checkedState.checkedStation,
              });
            }}
          />
        )}
        {industryModal.isOpen && (
          <IndustryModal
            show={industryModal.isOpen}
            onClickClose={industryModal.onClose}
            onClickSearch={(checkedState) => {
              onClickIndustryModalSettingButton({
                lList: checkedState.L,
                sList: checkedState.S,
              });
              industryModal.onClose();
            }}
            onChangeSelectedIndustry={(checkedState) => {
              updateModalJobCount({
                industryLId: checkedState.L,
                industrySId: checkedState.S,
              });
            }}
            displayIndustryData={createIndustryDisplayData(
              masterData.industry,
              industryModalJobCount
            )}
            matchJobCount={modalTotalCount}
            selectedIndustryIdList={{
              L: selectedCurrentCondition.industryLId,
              S: selectedCurrentCondition.industrySId,
            }}
          />
        )}
      </ModalOverlay>
    </>
  );
};
